/* global DEV_BASE_AUTH_URL__ */
import { saveAs } from 'filesaver.js';
import { CALL_API } from 'redux-api-middleware';
import { stringify } from 'qs';

const isoFetch = require('isomorphic-fetch');

export type GraphqlRequest = { query: string, variables?: Object }
export type ActionTypes = [any]
export type CallApi = Object

export type FetchOptions = {
  endpoint?: string,
  method?: string,
  body?: any,
  credentials?: string,
  headers?: Object,
  types?: ActionTypes
}

const DEFAULTS: FetchOptions = {
  method: 'get',
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': true,
    'X-Auth-Token': 'secret admin token',
  },
};

export const fetchAuth = ({ endpoint = '', ...options }: FetchOptions): CallApi => ({
  [CALL_API]: Object.assign(
    {},
    DEFAULTS,
    { endpoint: __DEV_BASE_AUTH_URL__ ? `${__DEV_BASE_AUTH_URL__}/auth/${endpoint}` : `/auth/${endpoint}` },
    options,
  ),
});

export const fetchApi = (options: FetchOptions): CallApi => fetchAuth({
  ...options,
  endpoint: `api/v0/submission/${options.endpoint}`,
});

export const fetchGraphql = (types: ActionTypes, graphql: GraphqlRequest): CallApi => fetchApi({
  types,
  endpoint: 'graphql',
  method: 'post',
  body: JSON.stringify(graphql),
});

export const fetchQC = ({
  format,
  endpoint = '',
  params = {},
  program,
  project,
  types,
}) => {
  const url = `${program}/${project}/qcreport/${endpoint}${
      Object.keys(params).length
        ? `?${stringify(params, { encode: false })}`
        : ''
    }`;

  return format
  ? { // Payload will be empty, unless we add Thunk
    payload: downloadViaAjax({ url }),
    type: types,
  }
  : fetchApi({
    endpoint: url,
    types,
  });
};

/* Old Fetch methods */

export const fetch = (opts: Object): Object => isoFetch(opts.url, {
  ...DEFAULTS,
  ...opts,
});

export const auth = (opts: Object): Object => fetch({
  ...opts,
  url: __DEV_BASE_AUTH_URL__ ? `${__DEV_BASE_AUTH_URL__}/auth/${opts.url}` : `/auth/${opts.url}`
});

export const api = (opts: Object): Object => auth({ ...opts, url: `api/v0/submission/${opts.url}` });
export const gql = async (query: Object): Object => {
  const response = await api({
    url: 'graphql',
    method: 'post',
    body: JSON.stringify(query),
  });
  return response.json();
};

export const download = (body: string, format: string, filename: string): void => {
  const type = {
    JSON: 'application/json',
    TSV: 'text/tab-separated-values',
    CSV: 'text/csv',
    XML: 'text/xml',
    BLOB: 'octet/stream',
  }[format.toUpperCase()];

  saveAs(new Blob([body], { type }), filename);
};

export const downloadViaAjax = async (
  ajaxConfig: Object,
  format: string = '',
  defaultFileName: string = 'unnamed_file',
): Promise => {
  const asText = async resp => resp.text();
  const read = {
    tsv: asText,
    csv: asText,
    xml: asText,
    json: async resp => JSON.stringify(await resp.json(), null, 2),
    blob: async resp => resp.blob(),
  };

  const response = await api(ajaxConfig);
  const fileName = (response.headers.get('content-disposition') || '')
    .replace('attachment; filename=', '').trim();
  const targetType = ['.tar', '.gz'].some(ext => fileName.includes(ext))
    ? 'blob'
    : format.length > 0
      ? format.toLowerCase()
      : fileName.split('.').pop();
  const content = await read[targetType || 'blob'](response);

  download(content, targetType, (fileName.length > 0) ? fileName : defaultFileName);
  return content;
};
